import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { UserX, Info, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/trpc";
import { JobPostDecisionReason } from "@api/shared-runtime/enums";
import { WITHDRAWAL_REASONS } from "./withdrawal-reasons";

export const JobPostWithdrawDialog: React.FC<{
  jobId: string;
  jobTitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}> = ({ jobId, jobTitle, open, onOpenChange, onSuccess }) => {
  const trpc = useTRPC();
  const [selectedReasons, setSelectedReasons] = useState<
    JobPostDecisionReason[]
  >([]);
  const [description, setDescription] = useState("");

  const {
    mutateAsync: setDecision,
    isError,
    isPending,
    reset,
  } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: () => {
        toast("Application withdrawn", {
          description:
            "You're no longer being considered for this position. You can re-express interest from the job post anytime.",
          duration: 4000,
          icon: <Check className="h-5 w-5 text-green-500" />,
        });
        setSelectedReasons([]);
        setDescription("");
        onSuccess?.();
        onOpenChange(false);
      },
    }),
  );

  const toggleReason = (id: JobPostDecisionReason): void => {
    setSelectedReasons((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const handleWithdraw = async (): Promise<void> => {
    await setDecision({
      id: jobId,
      interested: false,
      decisionReasonIds: selectedReasons,
      description: description.trim() || undefined,
    });
  };

  const handleOpenChange = (next: boolean): void => {
    if (isPending) {
      return;
    }
    if (!next) {
      setSelectedReasons([]);
      setDescription("");
      reset();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="[&.dialog-content]:max-w-[min(32rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-full rounded-2xl border-slate-200 bg-white text-slate-900 sm:max-w-lg sm:rounded-2xl p-0 overflow-y-auto gap-0">
        {/* Header band */}
        <div className="px-6 pt-6 pb-5 bg-red-50">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100">
              <UserX size={20} className="text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold text-slate-900">
                Withdraw your application?
              </DialogTitle>
              {jobTitle && (
                <p className="text-sm font-medium text-red-700 mt-0.5 truncate">
                  {jobTitle}
                </p>
              )}
              <DialogDescription className="mt-1 text-sm text-slate-600 leading-relaxed">
                You're about to let the organization know you're no longer
                interested in this position.
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Consequences strip */}
        <div className="px-6 py-4 border-t border-red-100/60 bg-white">
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <Info size={14} className="mt-0.5 text-slate-400 shrink-0" />
              <span>
                Your hiring-process progress will be saved in case you change
                your mind.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Info size={14} className="mt-0.5 text-slate-400 shrink-0" />
              <span>
                The organization will see you're no longer interested.
              </span>
            </li>
          </ul>
        </div>

        {/* Reason picker */}
        <div className="px-6 py-4 border-t border-slate-100 max-h-[40vh] overflow-y-auto">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Reason
            </span>
            <span className="text-xs text-slate-400">Optional</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {WITHDRAWAL_REASONS.map((r) => {
              const selected = selectedReasons.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => toggleReason(r.id)}
                  aria-pressed={selected}
                  disabled={isPending}
                  className={cn(
                    "inline-flex items-center gap-1.5 min-h-12 px-3 py-2 rounded-xl text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:pointer-events-none",
                    selected
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-slate-400 bg-white text-slate-700 hover:border-slate-500 hover:bg-slate-50",
                  )}
                >
                  {selected && <Check size={13} />}
                  {r.label}
                </button>
              );
            })}
          </div>

          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Anything else you'd like the organization to know? (optional)"
            rows={2}
            maxLength={500}
            disabled={isPending}
            className="mt-3 resize-none text-sm"
          />
        </div>

        {/* Error */}
        {isError && (
          <Alert variant="destructive" className="mx-6 mb-2">
            <AlertTitle>Couldn't withdraw</AlertTitle>
            <AlertDescription>
              Something went wrong. Please try again, or contact support if this
              keeps happening.
            </AlertDescription>
          </Alert>
        )}

        {/* Footer */}
        <div className="px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-slate-100 bg-slate-50/60">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleWithdraw}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Withdrawing…
              </>
            ) : (
              "Withdraw application"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
